import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faBuilding, faUser, faPhone, faEnvelope, faSpinner, 
    faAngleLeft, faAngleRight, faCalendarAlt, faPlus, faMapMarkerAlt, faIdCard, faLock,
    faFileContract, faCloudUploadAlt, faTrash, faFileAlt, faTimes
} from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import CustomMultiSelect from "../../../../common/formfields/CustomMultiSelect";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiClient from "../../../../../services/apiClient";
import apiServices from "../../../../../services/apiService";
import storageService from "../../../../../utils/storageService";

const VendorOnboardingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user } = useSelector((state) => state.auth);
    
    const roleName = (user?.roleName || user?.RoleName || user?.role || user?.Role || storageService.getItem("roleName") || "").toString().toLowerCase();
    const roleId = Number(user?.roleId || user?.RoleId || storageService.getItem("roleId") || 0);
    const loggedInUnitId = Number(user?.branchorunitID || 0);
    const isSiteAdmin = loggedInUnitId <= 0 || roleId === 3 || roleName.includes("site admin") || roleName.includes("super");
    const assignedSiteId = user?.siteId || storageService.getItem("siteId") || 2;
    const assignedUnitId = isSiteAdmin ? null : loggedInUnitId;

    const vendorId = searchParams.get('vendorId') || location.state?.vendorId || location.state?.vendorData?.vendorId || location.state?.vendorData?.id;
    const isEditMode = Boolean(vendorId);

    const [formData, setFormData] = useState({
        vendorName: "",
        contactPerson: "",
        mobileNumber: "",
        email: "",
        address: "",
        contractStartDate: "",
        contractEndDate: "",
        gstNumber: "",
        panNumber: "",
        categoryName: "",
        subCategoryName: [], // Array of option objects for CustomMultiSelect
        agreementDocUrl: "",
        isApprovalRequired: true,
        allowStaffLogin: false,
        vendorType: "Full-time"
    });

    const [docFileName, setDocFileName] = useState("");
    const [docFileSize, setDocFileSize] = useState("");
    const [scope, setScope] = useState(assignedUnitId ? "units" : "site");
    const [units, setUnits] = useState([]);
    const [existingVendorId, setExistingVendorId] = useState(null);
    const [contracts, setContracts] = useState([
        { unitId: assignedUnitId || null, contractStartDate: "", contractEndDate: "", agreementDocUrl: "" }
    ]);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);

    const [categoryModal, setCategoryModal] = useState({ isOpen: false, newCategoryName: "" });
    const [subCategoryModal, setSubCategoryModal] = useState({ isOpen: false, newSubCategoryName: "" });

    const fetchCategories = useCallback(async (customParams = {}) => {
        setFetchingCategories(true);
        try {
            // Build dynamic query params flexibly based on what is provided
            const params = {
                ...customParams
            };

            // Remove undefined, null, or empty string values
            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "")
            );

            const response = await apiClient.get('/vendor-management/vendor/GetCategories', {
                params: cleanParams
            });

            if (response.data?.isSuccess && response.data?.data) {
                const formatted = response.data.data.map(cat => ({
                    categoryId: cat.categoryId,
                    label: cat.categoryName,
                    value: cat.categoryName,
                    siteId: cat.siteId,
                    subCategories: (cat.subCategories || []).map(sub => ({
                        subCategoryId: sub.subCategoryId,
                        label: sub.subCategoryName,
                        value: sub.subCategoryName
                    }))
                }));
                setCategories(formatted);
            }
        } catch (error) {
            console.error("Failed to fetch categories", error);
            toast.error("Failed to fetch categories");
        } finally {
            setFetchingCategories(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Fetch vendor details if in Edit Mode
    useEffect(() => {
        const fetchVendorDetails = async () => {
            if (!vendorId) return;
            setLoading(true);
            try {
                const response = await apiClient.get('/vendor-management/vendor/GetVendors', {
                    params: {
                        siteId: assignedSiteId,
                        vendorId: vendorId
                    }
                });

                if (response.data?.isSuccess && response.data?.data) {
                    const vendor = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                    if (vendor) {
                        setFormData({
                            vendorName: vendor.vendorName || "",
                            contactPerson: vendor.contactPerson || "",
                            mobileNumber: vendor.mobileNumber || "",
                            email: vendor.email || "",
                            address: vendor.address || "",
                            contractStartDate: vendor.contractStartDate ? vendor.contractStartDate.split('T')[0] : "",
                            contractEndDate: vendor.contractEndDate ? vendor.contractEndDate.split('T')[0] : "",
                            gstNumber: vendor.gstNumber || "",
                            panNumber: vendor.panNumber || "",
                            categoryName: vendor.categoryName || "",
                            subCategoryName: (vendor.subCategories || []).map(sc => typeof sc === 'string' ? { label: sc, value: sc } : sc),
                            agreementDocUrl: vendor.agreementDocUrl || "",
                            isApprovalRequired: vendor.isApprovalRequired !== undefined ? vendor.isApprovalRequired : true,
                            allowStaffLogin: vendor.allowStaffLogin === true,
                            vendorType: vendor.vendorType || "Full-time"
                        });
                        const loaded = (vendor.contracts || []).map(item => ({
                            unitId: item.unitId || null,
                            contractStartDate: item.contractStartDate ? String(item.contractStartDate).split("T")[0] : "",
                            contractEndDate: item.contractEndDate ? String(item.contractEndDate).split("T")[0] : "",
                            agreementDocUrl: item.agreementDocUrl || ""
                        }));
                        if (loaded.length > 0) {
                            const visible = assignedUnitId
                                ? loaded.filter(item => String(item.unitId) === String(assignedUnitId))
                                : loaded;
                            const rows = visible.length > 0 ? visible : [{ unitId: assignedUnitId || null, contractStartDate: "", contractEndDate: "", agreementDocUrl: "" }];
                            setContracts(rows);
                            setScope(rows.some(item => !item.unitId) ? "site" : "units");
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch vendor details:", error);
                toast.error("Failed to load vendor details for editing");
            } finally {
                setLoading(false);
            }
        };

        fetchVendorDetails();
    }, [vendorId, assignedSiteId]);

    const validate = () => {
        let newErrors = {};
        if (!formData.vendorName) newErrors.vendorName = "Vendor Company Name is required";
        if (!formData.contactPerson) newErrors.contactPerson = "Contact Person is required";
        if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile Number is required";
        else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) newErrors.mobileNumber = "Invalid mobile number";
        if (!formData.email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
        if (!formData.categoryName) newErrors.categoryName = "Category is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleAddCategory = async () => {
        const categoryName = categoryModal.newCategoryName.trim();
        if (!categoryName) {
            toast.error("Category name cannot be empty");
            return;
        }
        try {
            const response = await apiClient.post("/vendor-management/vendor/CreateCategory", {
                categoryName,
                createdBy: user?.name || user?.Name || "SiteAdmin"
            });
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to add category");
                return;
            }
            const newCat = {
                categoryId: response.data?.id,
                label: categoryName,
                value: categoryName,
                subCategories: []
            };
            setCategories([...categories.filter((cat) => cat.value !== categoryName), newCat]);
            setFormData(prev => ({ ...prev, categoryName: newCat.value, subCategoryName: [] }));
            setCategoryModal({ isOpen: false, newCategoryName: "" });
            toast.success("Category added successfully");
        } catch (error) {
            console.error("Failed to add category", error);
            toast.error(error.response?.data?.message || "Failed to add category");
        }
    };

    const handleAddSubCategory = () => {
        if (!formData.categoryName) {
            toast.error("Please select a category first");
            return;
        }
        if (!subCategoryModal.newSubCategoryName.trim()) {
            toast.error("Subcategory name cannot be empty");
            return;
        }
        
        const newSubCat = { label: subCategoryModal.newSubCategoryName, value: subCategoryModal.newSubCategoryName };
        
        setCategories(categories.map(cat => {
            if (cat.value === formData.categoryName) {
                return { ...cat, subCategories: [...cat.subCategories, newSubCat] };
            }
            return cat;
        }));
        
        setFormData(prev => ({ ...prev, subCategoryName: [...prev.subCategoryName, newSubCat] }));
        setSubCategoryModal({ isOpen: false, newSubCategoryName: "" });
        toast.success("Subcategory added successfully");
    };

    useEffect(() => {
        if (!isSiteAdmin) return;
        apiServices({ method: "GET", url: `/Residential/ResUnitMaster/GetAllUnitMaster?siteId=${assignedSiteId}&pageNumber=1&pageSize=1000` })
            .then((res) => {
                const rows = res?.data?.data || res?.data || [];
                setUnits(Array.isArray(rows) ? rows : []);
            })
            .catch(() => setUnits([]));
    }, [isSiteAdmin, assignedSiteId]);

    const updateContract = (index, field, value) => {
        setContracts(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
    };

    const toggleUnit = (unitId) => {
        setContracts(prev => {
            const exists = prev.some(item => String(item.unitId) === String(unitId));
            if (exists) return prev.filter(item => String(item.unitId) !== String(unitId));
            return [...prev, { unitId, contractStartDate: "", contractEndDate: "", agreementDocUrl: "" }];
        });
    };

    const searchVendor = async () => {
        if (!formData.mobileNumber) return;
        const response = await apiClient.get("/vendor-management/vendor/SearchByMobile", {
            params: { siteId: assignedSiteId, mobileNumber: formData.mobileNumber }
        });
        const vendor = response.data?.data?.[0];
        if (!vendor) {
            setExistingVendorId(null);
            toast.info("No vendor found for this mobile. Fill the new profile.");
            return;
        }
        setExistingVendorId(vendor.vendorId);
        setFormData(prev => ({
            ...prev,
            vendorName: vendor.vendorName || "",
            contactPerson: vendor.contactPerson || "",
            email: vendor.email || "",
            address: vendor.address || "",
            gstNumber: vendor.gstNumber || "",
            panNumber: vendor.panNumber || "",
            categoryName: vendor.categoryName || "",
            subCategoryName: (vendor.subCategories || []).map(sc => typeof sc === "string" ? { label: sc, value: sc } : sc),
            vendorType: vendor.vendorType || prev.vendorType,
            allowStaffLogin: vendor.allowStaffLogin === true
        }));
        const mine = (vendor.contracts || []).find(item => String(item.unitId) === String(assignedUnitId));
        setContracts([{
            unitId: assignedUnitId,
            contractStartDate: mine?.contractStartDate ? String(mine.contractStartDate).split("T")[0] : "",
            contractEndDate: mine?.contractEndDate ? String(mine.contractEndDate).split("T")[0] : "",
            agreementDocUrl: mine?.agreementDocUrl || ""
        }]);
        toast.success("Vendor found. Add this unit contract.");
    };

    const contractPayload = () => contracts
        .filter(item => scope === "site" ? !item.unitId : item.unitId)
        .map(item => ({
            unitId: scope === "site" && isSiteAdmin ? null : Number(item.unitId),
            unitName: "",
            contractStartDate: item.contractStartDate || null,
            contractEndDate: item.contractEndDate || null,
            agreementDocUrl: item.agreementDocUrl || ""
        }));

    const handleDocUpload = (index) => (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File size must be under 10MB");
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => updateContract(index, "agreementDocUrl", reader.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveDoc = (index) => updateContract(index, "agreementDocUrl", "");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }

        setLoading(true);
        
        const selectedCategoryObj = categories.find(c => c.value === formData.categoryName);

        if (isEditMode || existingVendorId) {
            const saveVendorId = vendorId || existingVendorId;
            const updatePayload = {
                vendorId: Number(saveVendorId),
                siteId: assignedSiteId,
                categoryId: selectedCategoryObj?.categoryId || null,
                vendorName: formData.vendorName,
                contactPerson: formData.contactPerson,
                mobileNumber: formData.mobileNumber,
                email: formData.email,
                address: formData.address,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                agreementDocUrl: "",
                vendorType: formData.vendorType,
                contracts: contractPayload(),
                status: "Active",
                modifiedBy: roleName || "SiteAdmin",
                subCategoryName: formData.subCategoryName.map(sc => typeof sc === 'object' ? sc.value : sc),
                isApprovalRequired: formData.isApprovalRequired,
                allowStaffLogin: formData.allowStaffLogin
            };

            try {
                await apiClient.put(`/vendor-management/vendor/UpdateVendor?vendorId=${saveVendorId}`, updatePayload);
                toast.success("Vendor updated successfully!");
                navigate("/zentres/workforce-management/vendor");
            } catch (error) {
                console.error("Error updating vendor:", error);
                toast.error("Failed to update vendor. Please try again.");
            } finally {
                setLoading(false);
            }
        } else {
            const createPayload = {
                siteId: assignedSiteId,
                unitId: assignedUnitId || null,
                categoryId: selectedCategoryObj?.categoryId || null,
                categoryName: formData.categoryName,
                subCategoryName: formData.subCategoryName.map(sc => typeof sc === 'object' ? sc.value : sc),
                vendorName: formData.vendorName,
                contactPerson: formData.contactPerson,
                mobileNumber: formData.mobileNumber,
                email: formData.email,
                address: formData.address,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                agreementDocUrl: "",
                vendorType: formData.vendorType,
                contracts: contractPayload(),
                status: "Active",
                createdBy: roleName || "SiteAdmin",
                password: formData.password || "",
                personTypeId: 8250,
                isApprovalRequired: formData.isApprovalRequired,
                allowStaffLogin: formData.allowStaffLogin
            };

            try {
                await apiClient.post('/vendor-management/vendor/CreateVendor', createPayload);
                toast.success("Vendor onboarded successfully!");
                navigate("/zentres/workforce-management/vendor");
            } catch (error) {
                console.error("Error onboarding vendor:", error);
                toast.error("Failed to onboard vendor. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    const selectedCategoryObj = categories.find(c => c.value === formData.categoryName);
    const subCategoryOptions = selectedCategoryObj ? selectedCategoryObj.subCategories : [];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 pb-12 min-h-0">
            <Breadcrumbs items={["Workforce Management", "Vendor", isEditMode ? "Update Vendor" : "Onboard Vendor"]} />

            <div className="mb-6 mt-4">
                <PageHeadingSection
                    Navbar={true}
                    mainHeading={isEditMode ? "Update Vendor" : "Onboard New Vendor"}
                    subHeading={isEditMode ? "Modify vendor details, service categories, and contracts." : "Register a new vendor and automatically send login credentials."}
                />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col">
                <form onSubmit={handleSubmit} className="p-6 md:p-8 flex flex-col">
                    <div className="flex flex-col gap-8 max-w-3xl">
                        
                        {/* Categories Section - Reverted styling to match old form but keeping grids */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Category Name <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <CustomSelect
                                            options={[{label: "Select Category", value: ""}, ...categories.map(c => ({label: c.label, value: c.value}))]}
                                            value={formData.categoryName ? {label: formData.categoryName, value: formData.categoryName} : null}
                                            onChange={(val) => {
                                                setFormData(prev => ({ ...prev, categoryName: val.value, subCategoryName: [] }));
                                                if(errors.categoryName) setErrors(prev => ({ ...prev, categoryName: null }));
                                            }}
                                            placeholder={fetchingCategories ? "Loading..." : "Select Category"}
                                            error={errors.categoryName}
                                        />
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setCategoryModal({ isOpen: true, newCategoryName: "" })}
                                        className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md text-xs font-semibold hover:bg-blue-100 transition-colors shrink-0 border border-blue-100"
                                        title="Add New Category"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                                {errors.categoryName && <p className="text-red-500 text-[10px] mt-1">{errors.categoryName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Subcategories
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <CustomMultiSelect
                                            options={subCategoryOptions}
                                            value={formData.subCategoryName}
                                            onChange={(val) => setFormData(prev => ({ ...prev, subCategoryName: val }))}
                                            placeholder="Select Subcategories"
                                            isDisabled={!formData.categoryName}
                                        />
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setSubCategoryModal({ isOpen: true, newSubCategoryName: "" })}
                                        disabled={!formData.categoryName}
                                        className={`px-3 py-2 rounded-md text-xs font-semibold transition-colors shrink-0 border ${!formData.categoryName ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100'}`}
                                        title="Add New Subcategory"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Basic Info Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="vendorName">
                                    Vendor / Company Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faBuilding} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.vendorName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="vendorName" value={formData.vendorName} onChange={handleInputChange} 
                                        placeholder="e.g. Acme Services" 
                                    />
                                </div>
                                {errors.vendorName && <p className="text-red-500 text-[10px] mt-1">{errors.vendorName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="contactPerson">
                                    Contact Person <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.contactPerson ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="contactPerson" value={formData.contactPerson} onChange={handleInputChange} 
                                        placeholder="e.g. Rahul Sharma" 
                                    />
                                </div>
                                {errors.contactPerson && <p className="text-red-500 text-[10px] mt-1">{errors.contactPerson}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="mobileNumber">
                                    Primary Mobile <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faPhone} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 ${!isSiteAdmin && !isEditMode ? "pr-20" : "pr-3"} py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.mobileNumber ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} 
                                        placeholder="10-digit mobile number" maxLength={10}
                                    />
                                    {!isSiteAdmin && !isEditMode && (
                                        <button type="button" onClick={searchVendor} className="absolute right-1 top-1 bottom-1 px-2 text-xs font-semibold text-blue-600">Search</button>
                                    )}
                                </div>
                                {errors.mobileNumber && <p className="text-red-500 text-[10px] mt-1">{errors.mobileNumber}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="email">
                                    Primary Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.email ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="email" id="email" value={formData.email} onChange={handleInputChange} 
                                        placeholder="vendor@company.com" 
                                    />
                                </div>
                                {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="address">
                                    Company Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 pt-2.5 pointer-events-none">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <textarea 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        id="address" rows="2" value={formData.address} onChange={handleInputChange} 
                                        placeholder="123 Business Park, Mumbai" 
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="vendorType">Vendor type <span className="text-red-500">*</span></label>
                            <select id="vendorType" value={formData.vendorType} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary border-gray-200 dark:border-bordercolor">
                                <option value="Full-time">Full-time</option>
                                <option value="Part-Time">Part-Time</option>
                            </select>
                        </div>

                        {isSiteAdmin && !isEditMode && (
                            <div className="flex gap-4 text-sm">
                                <label className="flex items-center gap-2"><input type="radio" checked={scope === "site"} onChange={() => { setScope("site"); setContracts([{ unitId: null, contractStartDate: "", contractEndDate: "", agreementDocUrl: "" }]); }} /> Site</label>
                                <label className="flex items-center gap-2"><input type="radio" checked={scope === "units"} onChange={() => { setScope("units"); setContracts([]); }} /> Units</label>
                            </div>
                        )}

                        {isSiteAdmin && scope === "units" && (
                            <div className="flex flex-wrap gap-2">
                                {units.map(unit => {
                                    const id = unit.id || unit.unitId || unit.UnitId;
                                    const name = unit.unitName || unit.UnitName || unit.unitNumber || id;
                                    const checked = contracts.some(item => String(item.unitId) === String(id));
                                    return <label key={id} className="text-xs border rounded px-2 py-1"><input type="checkbox" checked={checked} onChange={() => toggleUnit(id)} /> {name}</label>;
                                })}
                            </div>
                        )}

                        {contracts.map((contract, index) => (
                            <div key={`${contract.unitId || "site"}-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-4 border rounded-md p-3">
                                <div className="md:col-span-3 text-xs font-semibold">{contract.unitId ? (units.find(unit => String(unit.id || unit.unitId || unit.UnitId) === String(contract.unitId))?.unitName || `Unit ${contract.unitId}`) : "Site contract"}</div>
                                <input type="date" value={contract.contractStartDate} onChange={(e) => updateContract(index, "contractStartDate", e.target.value)} className="px-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary border-gray-200 dark:border-bordercolor" />
                                <input type="date" value={contract.contractEndDate} onChange={(e) => updateContract(index, "contractEndDate", e.target.value)} className="px-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary border-gray-200 dark:border-bordercolor" />
                                <div>
                                    {contract.agreementDocUrl ? (
                                        <button type="button" onClick={() => handleRemoveDoc(index)} className="text-xs text-red-500">Remove agreement</button>
                                    ) : (
                                        <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={handleDocUpload(index)} className="text-xs" />
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Contract & Tax Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="gstNumber">
                                    GST Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="text" id="gstNumber" value={formData.gstNumber} onChange={handleInputChange} 
                                        placeholder="e.g. 27AADCC1234E1Z5" 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="panNumber">
                                    PAN Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="text" id="panNumber" value={formData.panNumber} onChange={handleInputChange} 
                                        placeholder="e.g. AADCC1234E" 
                                    />
                                </div>
                            </div>

                        </div>

                        {/* Policy Settings Section */}
                        {isSiteAdmin && (
                            <div className="pt-4 border-t border-gray-100 dark:border-bordercolor">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={formData.isApprovalRequired}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isApprovalRequired: e.target.checked }))}
                                        />
                                        <div className={`block w-10 h-6 rounded-full transition-colors ${formData.isApprovalRequired ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                                        <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.isApprovalRequired ? 'transform translate-x-4' : ''}`}></div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-gray-700 dark:text-textsecondary">Require Staff Approval (Policy Engine)</div>
                                        <div className="text-[10px] text-gray-500">If enabled, staff members from this vendor will require approval when assigned to duty, unless overridden.</div>
                                    </div>
                                </label>
                            </div>
                        )}

                        <div className="pt-4 border-t border-gray-100 dark:border-bordercolor">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.allowStaffLogin}
                                    onChange={(e) => setFormData(prev => ({ ...prev, allowStaffLogin: e.target.checked }))}
                                    className="w-4 h-4"
                                />
                                <div>
                                    <div className="text-xs font-bold text-gray-700 dark:text-textsecondary">Allow staff login</div>
                                    <div className="text-[10px] text-gray-500">When this is on, a login can be created later for each staff person of this vendor.</div>
                                </div>
                            </label>
                        </div>

                    </div>

                    <div className="flex justify-between mt-10 border-t pt-4 shrink-0 max-w-3xl">
                        <button
                            type="button"
                            onClick={() => navigate("/zentres/workforce-management/vendor")}
                            className="px-3 py-2 border border-gray-200 rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                                style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                    e.currentTarget.style.color = '#FFFFFF';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                    e.currentTarget.style.color = "var(--color-primaryaccent)";
                                }}
                            >
                                {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Vendor' : 'Onboard Vendor')}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Modal for Adding New Category */}
            {categoryModal.isOpen && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full mx-4 border border-gray-100">
                        <h2 className="text-lg font-bold mb-4 text-gray-800">Add New Category</h2>
                        <input
                            type="text"
                            value={categoryModal.newCategoryName}
                            onChange={(e) => setCategoryModal({ ...categoryModal, newCategoryName: e.target.value })}
                            placeholder="Enter category name"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => setCategoryModal({ isOpen: false, newCategoryName: "" })}
                                className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddCategory}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            >
                                Add Category
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Adding New Subcategory */}
            {subCategoryModal.isOpen && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full mx-4 border border-gray-100">
                        <h2 className="text-lg font-bold mb-1 text-gray-800">Add Subcategory</h2>
                        <p className="text-xs text-gray-500 mb-4">Adding to: <span className="font-semibold">{typeof formData.categoryName === 'string' ? formData.categoryName : ''}</span></p>
                        <input
                            type="text"
                            value={subCategoryModal.newSubCategoryName}
                            onChange={(e) => setSubCategoryModal({ ...subCategoryModal, newSubCategoryName: e.target.value })}
                            placeholder="Enter subcategory name"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => setSubCategoryModal({ isOpen: false, newSubCategoryName: "" })}
                                className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddSubCategory}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            >
                                Add Subcategory
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default VendorOnboardingForm;
